import { useEffect, useState, useLayoutEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Mail, Bot, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { handleSubscriptionUpgrade } from "@/lib/subscriptionUtils";

const API_URL = import.meta.env.VITE_API_URL || 'https://localhost:5001/api';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add("sophie-theme");

    return () => {
      root.classList.remove("sophie-theme");
    };
  }, []);

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      const subscription = searchParams.get('subscription');
      
      if (!token) {
        setVerificationStatus('error');
        setErrorMessage('No verification token provided');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/auth/verify-email?token=${token}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (data.success) {
          setVerificationStatus('success');
          toast.success("Email verified successfully!");
          
          // Handle subscription upgrade if provided in URL
          if (subscription && ['1', '2', '3', '4', '5'].includes(subscription)) {
            try {
              localStorage.setItem('selected-subscription', subscription);
              await handleSubscriptionUpgrade();
            } catch (error) {
              console.error('Error handling subscription upgrade:', error);
            }
          }
        } else {
          setVerificationStatus('error');
          setErrorMessage(data.message || 'Verification failed');
        }
      } catch (error) {
        setVerificationStatus('error');
        setErrorMessage('Failed to verify email. Please try again.');
        console.error('Verification error:', error);
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleSignIn = () => {
    const subscription = searchParams.get('subscription');
    const signinPath = subscription && ['1', '2', '3', '4', '5'].includes(subscription) 
      ? `/login?subscription=${subscription}` 
      : '/login';
    navigate(signinPath);
  };

  const handleResendEmail = () => {
    // Redirect to login to request new verification
    navigate('/login');
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md p-8 card-hover">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mb-4">
            {verificationStatus === 'loading' && <Loader2 className="w-8 h-8 animate-spin text-primary-foreground" />}
            {verificationStatus === 'success' && <CheckCircle className="w-8 h-8 text-primary-foreground" />}
            {verificationStatus === 'error' && <XCircle className="w-8 h-8 text-primary-foreground" />}
          </div>
          <h1 className="text-2xl font-semibold mb-2 text-center">
            {verificationStatus === 'loading' && 'Verifying...'}
            {verificationStatus === 'success' && 'Email Verified'}
            {verificationStatus === 'error' && 'Verification Failed'}
          </h1>
          <p className="text-muted-foreground text-center">
            {verificationStatus === 'loading' && 'Please wait while we verify your email address.'}
            {verificationStatus === 'success' && 'Your email has been verified. You can now sign in.'}
            {verificationStatus === 'error' && errorMessage}
          </p>
        </div>

        {verificationStatus === 'success' && (
          <div className="space-y-4">
            <div className="bg-green-500/10 text-green-600 p-4 rounded-lg flex items-center justify-center space-x-2 text-sm">
              <CheckCircle className="h-4 w-4" />
              <span>Successfully verified!</span>
            </div>
            <Button onClick={handleSignIn} className="w-full h-12 text-base">
              Sign In
            </Button>
          </div>
        )}

        {verificationStatus === 'error' && (
          <div className="space-y-4">
             <div className="bg-red-500/10 text-red-600 p-4 rounded-lg flex items-center justify-center space-x-2 text-sm">
              <XCircle className="h-4 w-4" />
              <span>Verification could not be completed</span>
            </div>
            <div className="space-y-3">
              <Button onClick={handleSignIn} className="w-full h-12 text-base">
                Go to Sign In
              </Button>
              <Button onClick={handleResendEmail} variant="outline" className="w-full h-12 text-base">
                <Mail className="mr-2 h-4 w-4" />
                Request New Verification Email
              </Button>
            </div>
          </div>
        )}

        {verificationStatus === 'loading' && (
           <div className="flex justify-center py-4">
             <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
           </div>
        )}
      </Card>
    </div>
  );
}
